<script lang="ts">
  import { Seguros, Cuentas, type Aseguradora, type CoberturaResult, type Cuenta } from '../lib/api';

  let lista = $state<Aseguradora[]>([]);
  let nueva = $state<Aseguradora>({ codigo:'', nombre:'', nit:'', contacto:'', telefono:'', email:'', porcentajeDefault: 80, activa: true });
  let editandoId = $state<string | null>(null);
  let cargando = $state(false);
  let error = $state<string|null>(null);

  // Cobertura
  let cuentas = $state<Cuenta[]>([]);
  let cuentaId = $state('');
  let pctOverride = $state<number|null>(null);
  let resultado = $state<CoberturaResult|null>(null);

  async function recargar() {
    cargando = true; error = null;
    try {
      lista = await Seguros.list(true);
    } catch (e: any) { error = e.message; }
    cargando = false;
  }
  async function recargarCuentas() {
    try { cuentas = await Cuentas.list(); } catch (e: any) { error = e.message; }
  }
  function editar(a: Aseguradora) {
    editandoId = a.id ?? null;
    nueva = { codigo: a.codigo, nombre: a.nombre, nit: a.nit ?? '', contacto: a.contacto ?? '', telefono: a.telefono ?? '', email: a.email ?? '', porcentajeDefault: a.porcentajeDefault, activa: a.activa ?? true };
  }

  function cancelarEdicion() {
    editandoId = null;
    nueva = { codigo:'', nombre:'', nit:'', contacto:'', telefono:'', email:'', porcentajeDefault: 80, activa: true };
  }

  async function guardar() {
    error = null;
    if (!nueva.codigo || !nueva.nombre) { error = 'Código y nombre requeridos'; return; }
    try {
      if (editandoId) {
        await Seguros.actualizar(editandoId, nueva);
      } else {
        await Seguros.crear(nueva);
      }
      nueva = { codigo:'', nombre:'', nit:'', contacto:'', telefono:'', email:'', porcentajeDefault: 80, activa: true };
      editandoId = null;
      await recargar();
    } catch (e: any) { error = e.message; }
  }
  async function eliminarAseguradora(a: Aseguradora) {
    if (!confirm(`¿Eliminar a ${a.nombre}?`)) return;
    try { await Seguros.eliminar(a.id!); await recargar(); }
    catch (e: any) { error = e.message; }
  }
  async function calcular() {
    error = null; resultado = null;
    if (!cuentaId) { error = 'Selecciona una cuenta'; return; }
    try {
      const body = pctOverride != null ? { porcentajeCobertura: pctOverride } : {};
      resultado = await Seguros.calcular(cuentaId, body);
    } catch (e: any) { error = e.message; }
  }

  $effect(() => { recargar(); recargarCuentas(); });
</script>

<section>
  <header class="view-header">
    <div><h2>Aseguradoras / Seguros</h2><p class="muted">Catálogo de aseguradoras y cálculo de cobertura</p></div>
    <button onclick={recargar} disabled={cargando}>Recargar</button>
  </header>

  {#if error}<div class="err">{error}</div>{/if}

  <h3>{editandoId ? 'Editar aseguradora' : 'Nueva aseguradora'}</h3>
  <div class="form">
    <label class="field"><span>Código</span><input placeholder="Código" bind:value={nueva.codigo}/></label>
    <label class="field"><span>Nombre</span><input placeholder="Nombre" bind:value={nueva.nombre}/></label>
    <label class="field"><span>NIT</span><input placeholder="NIT" bind:value={nueva.nit}/></label>
    <label class="field"><span>Contacto</span><input placeholder="Contacto" bind:value={nueva.contacto}/></label>
    <label class="field"><span>Teléfono</span><input placeholder="Teléfono" bind:value={nueva.telefono}/></label>
    <label class="field"><span>Email</span><input placeholder="Email" bind:value={nueva.email}/></label>
    <label class="field"><span>% cobertura</span>
      <input type="number" min="0" max="100" step="0.01" bind:value={nueva.porcentajeDefault}/>
    </label>
    <button onclick={guardar}>{editandoId ? 'Guardar cambios' : 'Crear'}</button>
    {#if editandoId}<button type="button" class="ghost" onclick={cancelarEdicion}>Cancelar</button>{/if}
  </div>

  <h3>Catálogo</h3>
  <table>
    <thead><tr><th>Código</th><th>Nombre</th><th>NIT</th><th>Tel.</th><th>% def.</th><th></th></tr></thead>
    <tbody>
      {#each lista as a (a.id)}
        <tr>
          <td>{a.codigo}</td>
          <td>{a.nombre}</td>
          <td>{a.nit ?? '—'}</td>
          <td>{a.telefono ?? '—'}</td>
          <td>{a.porcentajeDefault}%</td>
          <td><button onclick={() => editar(a)}>Editar</button> <button onclick={() => eliminarAseguradora(a)}>Eliminar</button></td>
        </tr>
      {/each}
      {#if lista.length === 0 && !cargando}
        <tr><td colspan="6">Sin aseguradoras.</td></tr>
      {/if}
    </tbody>
  </table>

  <h3>Calcular cobertura sobre cuenta</h3>
  <div class="form">
    <label class="field"><span>Cuenta</span>
      <select bind:value={cuentaId}>
        <option value="">— elegir —</option>
        {#each cuentas as c (c.id)}
          <option value={c.id}>{c.id?.slice(0,8)} · Q{c.total ?? 0} · {c.estado}</option>
        {/each}
      </select>
    </label>
    <label class="field"><span>% override</span>
      <input type="number" min="0" max="100" step="0.01" bind:value={pctOverride} placeholder="(usa el de la aseguradora)"/>
    </label>
    <button onclick={calcular}>Calcular</button>
  </div>

  {#if resultado}
    <div class="resultado">
      <strong>Resultado</strong>
      <ul>
        <li>Total: Q{resultado.total}</li>
        <li>Deducible: Q{resultado.deducible}</li>
        <li>Copago: Q{resultado.copago}</li>
        <li>% cobertura: {resultado.porcentajeCobertura}%</li>
        <li><strong>Paga aseguradora:</strong> Q{resultado.pagaAseguradora}</li>
        <li><strong>Paga paciente:</strong> Q{resultado.pagaPaciente}</li>
      </ul>
    </div>
  {/if}
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  button.ghost { background: #fff; color: #475569; border: 1px solid #e5e7eb; box-shadow: none; }
  button.ghost:hover:not(:disabled) { transform: none; background: #f1f5f9; }
  .err { background: #fee2e2; color: #dc2626; padding: .6rem; border-radius: 8px; margin: .4rem 0; }
  .form { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; margin: .6rem 0; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .form input, .form select { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  .form input:focus, .form select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  table { width: 100%; border-collapse: collapse; margin-top: .6rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { border-top: 1px solid #f3f4f6; padding: .5rem .8rem; text-align: left; color: #111827; }
  td button { padding: .3rem .6rem; border-radius: 6px; box-shadow: none; font-size: .8rem; }
  .resultado { background: #f0fdf4; border-left: 4px solid #16a34a; padding: .6rem 1rem; margin-top: .8rem; border-radius: 8px; }
  .resultado ul { margin: .3rem 0; }
  h3 { margin-top: 1.5rem; font-size: 1rem; color: #166534; }
</style>
